import sumBy from 'lodash/sumBy'
import { CAPABILITY_KEY, CARRIER, EXPRESS_CUSTOMS, PARCEL_CUSTOMS } from '~/basket/model/constants'
import { Basket, BasketShipment } from '~/basket/model/reducers'
import {
  Address,
  Customs,
  CustomsType,
  Package,
  Payment,
  Promocode,
  Receiver,
  Shipper,
  ShippingFee,
} from '~/basket/model/session/reducers'
import { post } from '~/util/api/post'
import { assertNever, notEmpty } from '~/util/helpers/ts-utils'
import { logException } from '~/logging'

interface Order {
  order: {
    shipper: ShipperDto
  }
  shipments: ShipmentDto[]
}

interface ShipmentDto {
  reference: string
  shipper: ShipperDto
  receiver: ReceiverDto
  parcelType: ParcelTypeDto
  customsForm: CustomsDto | null
  contentDescription: string | null
  frontEndId: string
  carrier: string
  trackerCode: string | null
  promoCode: PromoCodeDto | null
  options: ShipmentOptionDto[]
}

interface AddressDto {
  countryCode: string
  zipCode: string
  city: string
  street: string
  number: string
  addition: string
  isBusiness: boolean
  additionalAddressLine: string
}

interface NameDto {
  firstName: string
  lastName: string
  companyName: string | null
}

interface ParcelTypeDto {
  key: string
  minWeightKg: number
  maxWeightKg: number
  dimensions: { maxLengthCm: number; maxWidthCm: number; maxHeightCm: number; maxSumCm: number | null }
}

interface EmailDto {
  address: string
}

interface ShipperDto {
  address: AddressDto
  emailSubscription: boolean
  eori: string | null
  vatNumber: string | null
  phoneNumber: string | null
  name: NameDto
  email: EmailDto
}

interface CustomsDto {
  certificateNumber: string
  currency: string
  invoiceNumber: string
  licenceNumber: string
  exportType: string
  exportReason: string
  remarks: string
  totalValue: number
  totalWeight: number
  type: string
  shippingFee?: ShippingFee
  senderInboundVatNumber?: string
  products: CustomGoodDto[]
}

interface CustomGoodDto {
  code: string
  description: string
  origin: string
  quantity: number
  value: number
  weight: number
}

interface ReceiverDto {
  address: AddressDto
  phoneNumber: string | null
  name: NameDto
  email: EmailDto
}

interface PromoCodeDto {
  key: string
  discountOnShippingCosts: number
}

interface ShipmentOptionDto {
  key: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: any | null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createOrder(basket: Basket): any {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return postOrder(createOrderFromBasket(basket))
}

function postOrder(order: Order) {
  return post('/api/orders', order)
}

function createOrderFromBasket(basket: Basket): Order {
  return {
    order: {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      shipper: toShipperDto(basket.shipments[0].shipper, basket.originCountry, basket.payment.emailSubscription),
    },
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    shipments: basket.shipments.map((s) => toShipmentDto(s, basket)),
  }
}

function toShipmentDto(shipment: BasketShipment, basket: Basket): ShipmentDto {
  const { parcelTypeKey } = shipment.package

  if (!parcelTypeKey) {
    logException(new Error('No type key defined for parcel'))
  }

  return {
    reference: '',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    receiver: toReceiverDto(shipment.receiver, shipment.package),
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    shipper: toShipperDto(shipment.shipper, basket.originCountry, basket.payment.emailSubscription),
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    customsForm: toCustomsDto(shipment.customs, shipment.package.carrierKey!, shipment.shipper.address.countryCode),
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    contentDescription: toContent(shipment.receiver.contentDescription),
    parcelType: {
      // TODO sebastian: only key is used in backend, refactor this to key (must be backward compatible!)
      key: parcelTypeKey!,
      maxWeightKg: 0,
      minWeightKg: 0,
      dimensions: { maxHeightCm: 0, maxLengthCm: 0, maxWidthCm: 0, maxSumCm: null },
    },
    carrier: shipment.package.carrierKey!,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    options: toOptionDtos(basket, shipment),
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    promoCode: toPromoCodeDto(shipment.promocode),
    trackerCode: shipment.package.trackerCode,
    frontEndId: 'new',
  }
}

function toPromoCodeDto(promocode: Promocode | null): PromoCodeDto | null {
  if (promocode === null || promocode.discount === null) {
    return null
  }

  return {
    key: promocode.value,
    discountOnShippingCosts: promocode.discount.percentage,
  }
}

function toReceiverDto(receiver: Receiver, pack: Package): ReceiverDto {
  return {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    address: toAddressDto(receiver.address, pack.destinationCountry),
    phoneNumber: receiver.phoneNumber ? receiver.phoneNumber : null,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    name: toNameDto(receiver),
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    email: toEmailDto(receiver),
  }
}

function toShipperDto(shipper: Shipper, originCountry: string, emailSubscription: boolean): ShipperDto {
  return {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    address: toAddressDto(shipper.address, originCountry),
    emailSubscription,
    vatNumber: shipper.vatNumber ? shipper.vatNumber.toUpperCase() : null,
    phoneNumber: shipper.phoneNumber ? shipper.phoneNumber : null,
    eori: shipper.eoriNumber ? shipper.eoriNumber : null,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    name: toNameDto(shipper),
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    email: toEmailDto(shipper),
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function toCustomsDto(customs: Customs | null, carrierKey: string, originCountry: string): CustomsDto | null {
  if (!customs) {
    return null
  }

  const error = new Error(`Unknown carrier key (${carrierKey}) and customs type (${customs.customsType})`)

  const type: string = (() => {
    switch (carrierKey) {
      case CARRIER.PARCEL:
        switch (customs.customsType) {
          case CustomsType.NonCommercial:
            return PARCEL_CUSTOMS.GIFT
          case CustomsType.Commercial:
            return PARCEL_CUSTOMS.COMMERCIAL
          default:
            throw error
        }
      case CARRIER.EXPRESS_ECONOMY:
      case CARRIER.EXPRESS:
        switch (customs.customsType) {
          case CustomsType.NonCommercial:
            return EXPRESS_CUSTOMS.PROFORMA
          case CustomsType.Commercial:
            return EXPRESS_CUSTOMS.COMMERCIAL
          default:
            throw error
        }
      default:
        throw error
    }
  })()

  const shippingFee = customs.shippingFeeValue
    ? { currency: 'EUR', value: customs.shippingFeeValue }
    : { currency: 'EUR', value: 0 }

  return {
    certificateNumber: customs.certificate,
    currency: 'EUR',
    invoiceNumber: customs.invoice,
    licenceNumber: customs.license,
    exportType: customs.exportType,
    exportReason: customs.exportReason,
    products: customs.items.map(
      ({ commodityCode: code, description, countryOfOrigin: country, quantity, value, weight }) => ({
        code,
        description,
        origin: country,
        quantity: Number(quantity),
        value: Number(value),
        weight: Number(weight),
      })
    ),
    remarks: customs.remarks,
    totalValue: sumBy(customs.items, (i) => Number(i.value)),
    totalWeight: sumBy(customs.items, (i) => Number(i.weight)),
    shippingFee,
    senderInboundVatNumber: customs.senderInboundVatNumber,
    type,
  }
}

function toContent(description: string | null) {
  return description && description?.length >= 3 ? description : null
}

function toOptionDtos(basket: Basket, shipment: BasketShipment): ShipmentOptionDto[] {
  return [
    ...shipment.receiver.serviceOptionKeys,
    basket.payment.paymentOptionKey === 'PRINT' ? '' : basket.payment.paymentOptionKey,
    shipment.receiver.deliveryOptionKey,
    basket.payment.handOffKey,
    shipment.customs?.isExpressPaperlessTrade ? CAPABILITY_KEY.EXPRESS_PAPERLESS_TRADE : '',
  ]
    .filter(notEmpty)
    .map((key) => ({
      key,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      input: getInput(key),
    }))

  function getInput(key: string) {
    // These must be validated and correct at this point
    switch (key) {
      case CAPABILITY_KEY.PS:
        // eslint-disable-next-line no-case-declarations
        const value = shipment.receiver.servicepoint!

        switch (value.inputKind) {
          case 'PackStation':
            return `${value.servicepoint.servicepointKey}|${value.postNumber}`
          case 'Servicepoint':
            return value.servicepoint.servicepointKey
          default:
            return assertNever(value)
        }
      case CAPABILITY_KEY.PERS_NOTE:
        return shipment.receiver.personalNote
      case CAPABILITY_KEY.X_II:
        return shipment.receiver.insuranceValue
      case CAPABILITY_KEY.REFERENCE:
        return shipment.receiver.reference
      case CAPABILITY_KEY.X_PI:
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return toPickupInterval(basket.payment)
      case CAPABILITY_KEY.PICKUP:
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return toTimeWindow(basket.payment)
      default:
        return null
    }
  }

  function toTimeWindow({ timeWindow }: Payment) {
    return {
      date: timeWindow!.start.format('YYYY-MM-DD'),
      timePeriod: {
        startTime: timeWindow!.start.format('Hmm'),
        endTime: timeWindow!.end.format('Hmm'),
      },
    }
  }

  function toPickupInterval({ pickupDate, pickupRange }: Payment) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return `${pickupDate}T${convertDecimalToISOTime(pickupRange!.min)}/${pickupDate}T${convertDecimalToISOTime(
      pickupRange!.max
    )}`
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  function convertDecimalToISOTime(hours: number): string {
    const sign = hours < 0 ? '-' : ''
    const hrs = Math.floor(Math.abs(hours))
    const mins = Math.floor((Math.abs(hours) * 60) % 60)
    return `${sign + (hrs < 10 ? '0' : '') + hrs}:${mins < 10 ? '0' : ''}${mins}`
  }
}

function toAddressDto(address: Address, countryCode: string): AddressDto {
  return {
    countryCode,
    number: address.houseNumber,
    addition: address.houseNumberSuffix,
    street: address.street,
    city: address.city,
    zipCode: address.postalCode,
    isBusiness: false,
    additionalAddressLine: address.additionalAddressLine || '',
  }
}

function toNameDto<T extends NameDto>(name: T) {
  return {
    firstName: name.firstName,
    lastName: name.lastName,
    companyName: name.companyName === null || name.companyName ? name.companyName : null,
  }
}

function toEmailDto<T extends { email: string }>(emailHolder: T) {
  return {
    address: emailHolder.email,
  }
}
